import { useEffect, type ReactNode } from 'react';
import { Stack } from '@mui/material';
import { theme } from '@theguarantors/tg-package-material-ui';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';

import type { AddressFieldConfig } from '../../config';
import type { SectionKey } from '../../lease-details.types';
import { FieldHelperText } from '../../components/field-helper.text';
import { LeaseAddressInput } from '../../inputs/lease-address-input';
import { useLeaseDetailsUI } from '../../lease-details-ui.context';
import { useFieldEventContextBuilder } from '../use-field-event-context';
import { firstErrorMessage, getNestedError } from '../utils';
import {
  AddressesAutocomplete,
  formatAddressListValue,
  useAddresses,
} from '@/components/AddressesAutocomplete';
import type { AddressAutocompleteOption } from '@/utils/types/addressAutocompleteOption.interface';
import { type SkeletonConfig } from '../../config/field.config';

// Disabled look for the linked address autocomplete. Unlike a plain input (where the <input> fills
// the field), the autocomplete's input stops short of the dropdown chevron, so we style the input
// ROOT — not the inner input — else the tg theme's disabled grey (neutralGrey[50]) shows by the chevron.
const disabledAddressInputSx = {
  '.MuiInputLabel-root': { color: theme.palette.neutralGrey[300] },
  '& .MuiInputBase-root.Mui-disabled': {
    backgroundColor: 'white !important',
    color: theme.palette.neutralGrey[300],
  },
};

export type AddressFieldProps = {
  path: string;
  field: AddressFieldConfig;
  sectionKey: SectionKey;
  arrayIndex?: number;
  required: boolean;
  disabled: boolean;
  label: ReactNode;
  helperText?: ReactNode;
};

export function AddressField({
  path,
  field,
  sectionKey,
  arrayIndex,
  required,
  disabled,
  label,
  helperText,
}: AddressFieldProps) {
  const buildContext = useFieldEventContextBuilder<unknown>(path, sectionKey, arrayIndex);

  if (!field.linking) {
    return (
      <LeaseAddressInput
        name={path}
        label={typeof label === 'string' ? label : undefined}
        required={required}
        disabled={disabled}
        skeleton={field.skeleton}
        onSelect={
          field.events?.onChange
            ? (option) => field.events?.onChange?.(buildContext(option))
            : undefined
        }
      />
    );
  }

  return (
    <LinkedAddressField
      path={path}
      linking={field.linking}
      skeleton={field.skeleton}
      label={label}
      required={required}
      disabled={disabled}
      helperText={helperText}
      onSelect={
        field.events?.onChange
          ? (option) => field.events?.onChange?.(buildContext(option))
          : undefined
      }
    />
  );
}

type LinkedAddressFieldProps = {
  path: string;
  linking: NonNullable<AddressFieldConfig['linking']>;
  skeleton?: SkeletonConfig;
  label: ReactNode;
  required: boolean;
  disabled: boolean;
  helperText?: ReactNode;
  onSelect?: (option: AddressAutocompleteOption | null) => void;
};

function LinkedAddressField({
  path,
  linking,
  skeleton,
  label,
  required,
  disabled,
  helperText: staticHelperText,
  onSelect,
}: LinkedAddressFieldProps) {
  const { setValue, getValues, trigger, control } = useFormContext();
  const { addresses } = useAddresses();
  const { asteriskColor } = useLeaseDetailsUI();
  const resolvedAsteriskColor = asteriskColor ?? theme.palette.red[600];

  // Placeholder names keep `useWatch` scoped when an optional link isn't configured.
  const [buildingId, propertyId, sdrEnrolled] = useWatch({
    control,
    name: [
      linking.buildingIdField,
      linking.propertyIdField ?? '__no_property_id__',
      linking.sdrEnrolledField ?? '__no_sdr_enrolled__',
    ],
  }) as [number | undefined, string | undefined, boolean | undefined];

  useEffect(() => {
    if (
      !linking.hydratePropertyIdFromAddresses ||
      !linking.propertyIdField ||
      propertyId ||
      !buildingId ||
      !addresses
    ) {
      return;
    }

    const matched = addresses.find((address) => address.monolith_building_id === buildingId);
    if (matched?.property_id) {
      setValue(linking.propertyIdField, matched.property_id);
    }
  }, [
    addresses,
    buildingId,
    linking.hydratePropertyIdFromAddresses,
    linking.propertyIdField,
    propertyId,
    setValue,
  ]);

  useEffect(() => {
    if (!linking.sdrEnrolledField || !buildingId || !addresses || sdrEnrolled !== undefined) {
      return;
    }
    const matched = addresses.find((address) => address.monolith_building_id === buildingId);
    if (matched?.is_sdr_enrolled !== undefined) {
      setValue(linking.sdrEnrolledField, matched.is_sdr_enrolled);
    }
  }, [addresses, buildingId, linking.sdrEnrolledField, sdrEnrolled, setValue]);

  const errorFieldNames = linking.errorFieldNames?.length ? linking.errorFieldNames : [path];
  const { errors } = useFormState({ control, name: [...errorFieldNames] });
  const errorMessage =
    errorFieldNames
      .map((fieldName) => firstErrorMessage(getNestedError(errors, fieldName)))
      .find(Boolean) ?? '';
  const hasError = errorMessage.length > 0;

  // User-initiated: mark dirty so exit guards and draft capture see an address-only edit.
  const setLinked = (fieldName: string, value: unknown) =>
    setValue(fieldName, value, { shouldDirty: true });

  const resetFieldValues = (values: Record<string, unknown> | undefined) => {
    if (!values) {
      return;
    }
    for (const [fieldName, value] of Object.entries(values)) {
      setLinked(fieldName, value);
    }
  };

  const onBlurFields = linking.validateOnBlurFields?.length ? linking.validateOnBlurFields : [path];

  const handleChange = (addressOption: AddressAutocompleteOption | null) => {
    onSelect?.(addressOption);
    if (!addressOption) {
      setLinked(linking.buildingIdField, linking.clearSelection?.restoreBuildingId ?? 0);
      setLinked(path, linking.clearSelection?.restoreAddress ?? '');
      if (linking.propertyIdField) {
        setLinked(linking.propertyIdField, '');
      }
      if (linking.addressAlphaIdField) {
        setLinked(linking.addressAlphaIdField, '');
      }
      if (linking.propertyAlphaIdField) {
        setLinked(linking.propertyAlphaIdField, '');
      }
      if (linking.sdrEnrolledField) {
        setLinked(linking.sdrEnrolledField, undefined);
      }
      resetFieldValues(linking.clearSelection?.resetFields);
      void trigger([...onBlurFields]);
      return;
    }

    const isBuildingChange =
      addressOption.monolith_building_id !== getValues(linking.buildingIdField);
    setLinked(linking.buildingIdField, addressOption.monolith_building_id);
    setLinked(path, formatAddressListValue(addressOption));
    if (linking.propertyIdField) {
      setLinked(linking.propertyIdField, addressOption.property_id ?? '');
    }
    if (linking.addressAlphaIdField) {
      setLinked(linking.addressAlphaIdField, addressOption.address_alpha_id ?? '');
    }
    if (linking.propertyAlphaIdField) {
      setLinked(linking.propertyAlphaIdField, addressOption.property_alpha_id ?? '');
    }
    if (linking.sdrEnrolledField) {
      setLinked(linking.sdrEnrolledField, addressOption.is_sdr_enrolled);
    }
    if (isBuildingChange) {
      resetFieldValues(linking.resetFieldsOnBuildingChange);
    }
    if (addressOption.is_sdr_enrolled === false) {
      resetFieldValues(linking.resetFieldsWhenNotSdrEnrolled);
    }
    // Clear a stale "address required" error now rather than on the next blur.
    void trigger([...onBlurFields]);
  };

  return (
    <Stack direction="column" sx={{ width: '100%' }}>
      <AddressesAutocomplete
        data-testid={path}
        name={path}
        color="secondary"
        label={typeof label === 'string' ? label : undefined}
        required={required}
        disabled={disabled}
        // Red required asterisk when the form sets no asteriskColor (renewal); edit sets 'inherit'.
        // When disabled, match the disabled text inputs' white-bg look (renewal's greyed address).
        inputSx={[
          {
            '& .MuiFormLabel-asterisk': { color: `${resolvedAsteriskColor} !important` },
            // Disabled → grey asterisk (inherit the disabled label), like the text inputs.
            '& .MuiInputLabel-root.Mui-disabled .MuiFormLabel-asterisk': {
              color: 'inherit !important',
            },
          },
          ...(disabled ? [disabledAddressInputSx] : []),
        ]}
        value={buildingId ?? 0}
        skeleton={skeleton}
        error={hasError}
        helperText={errorMessage}
        onBlur={() => {
          void trigger([...onBlurFields]);
        }}
        onChange={handleChange}
      />
      {!hasError && staticHelperText != null && staticHelperText !== '' && (
        <FieldHelperText text={staticHelperText} disabled={disabled} />
      )}
    </Stack>
  );
}
